import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Radio, Palette, Timer, PlayCircle, Zap, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1627931539006-d5c4677e05ea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const CONSOLE_IMG =
  "https://images.unsplash.com/photo-1551710029-607e06bd45ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const MAILTO =
  "mailto:greg.norton@whiplashcreative.com?subject=The%20Stream%20Stop%20-%20Get%20Started";

const VALUE_PROPS = [
  { icon: Palette, title: "Custom Branding", desc: "Every client gets a fully white-labeled page — their colors, logo, background and fonts, live in minutes." },
  { icon: Timer, title: "Real-time Countdowns", desc: "Schedule go-live times and let a branded countdown build anticipation, then swap to the stream automatically." },
  { icon: PlayCircle, title: "Reliable Playback", desc: "Embed any professional player and deliver a smooth, responsive viewing experience on every device." },
  { icon: Zap, title: "Instant Updates", desc: "Push new titles, status notes and embeds mid-event — audiences stay current with no refresh." },
];

const LandingPage = () => {
  useEffect(() => {
    document.title = "The Stream Stop | Professional White-Label Livestreaming";
  }, []);
  return (
    <div className="wl-landing min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Top bar */}
      <header className="relative z-20 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-full bg-primary text-primary-foreground">
            <Radio size={20} />
          </span>
          <span className="font-[Oswald] uppercase tracking-widest text-lg text-foreground">The Stream Stop</span>
        </div>
        <a href="/admin" data-testid="admin-link" className="font-[Poppins] text-sm text-muted-foreground hover:text-primary transition-colors">
          Admin
        </a>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="landing-glow" aria-hidden="true" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="landing-rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-[Poppins] uppercase tracking-widest text-primary">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" /> Enterprise Livestreaming
            </span>
            <h1 className="mt-6 font-[Oswald] font-bold uppercase leading-[1.03] tracking-tight text-foreground text-4xl sm:text-5xl lg:text-6xl">
              Professional <span className="text-primary">White-Label</span> Livestreaming for Your Brand
            </h1>
            <p className="mt-6 font-[Poppins] text-base sm:text-lg text-muted-foreground max-w-xl">
              Launch a stunning, fully-branded broadcast page for every client and event — countdowns, custom players and real-time control, all under your name.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a href={MAILTO} data-testid="get-started-hero">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-[Oswald] uppercase tracking-wider text-base px-8 py-6 rounded-full group">
                  Get Started
                  <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <span className="font-[Poppins] text-sm text-muted-foreground">No setup fees · Go live fast</span>
            </div>
          </div>

          <div className="landing-rise" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl">
              <img src={HERO_IMG} alt="Live broadcast control room" className="w-full h-[300px] sm:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-[Oswald] uppercase tracking-widest text-primary-foreground">
                <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> On Air
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16 border-t border-border/60">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-[Oswald] uppercase tracking-wider text-foreground text-3xl sm:text-4xl">Built for the broadcast</h2>
          <p className="mt-3 font-[Poppins] text-muted-foreground">Everything you need to run a premium client livestream, out of the box.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUE_PROPS.map(({ icon: Icon, title, desc }) => (
            <div key={title} data-testid={`value-prop-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group rounded-xl border border-border bg-card p-6 transition-all hover:border-primary/60 hover:-translate-y-1">
              <span className="grid place-items-center h-12 w-12 rounded-lg bg-primary/15 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                <Icon size={24} />
              </span>
              <h3 className="mt-5 font-[Oswald] uppercase tracking-wide text-lg text-foreground">{title}</h3>
              <p className="mt-2 font-[Poppins] text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="relative overflow-hidden max-w-6xl mx-auto px-6 my-8">
        <div className="relative rounded-2xl border border-border overflow-hidden">
          <img src={CONSOLE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-background/85" />
          <div className="relative z-10 px-8 py-16 text-center">
            <h2 className="font-[Oswald] uppercase tracking-wider text-foreground text-3xl sm:text-4xl max-w-2xl mx-auto">
              Ready to put your brand <span className="text-primary">on air?</span>
            </h2>
            <p className="mt-4 font-[Poppins] text-muted-foreground max-w-xl mx-auto">
              Let's set up your first white-label event. Reach out and we'll get you streaming.
            </p>
            <a href={MAILTO} data-testid="get-started-cta" className="inline-block mt-8">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-[Oswald] uppercase tracking-wider text-base px-8 py-6 rounded-full group">
                Get Started
                <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 max-w-6xl mx-auto px-6 py-10 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="font-[Oswald] uppercase tracking-widest text-sm text-foreground">The Stream Stop</span>
        <span className="font-[Poppins] text-xs text-muted-foreground">© {new Date().getFullYear()} Whiplash Creative. All rights reserved.</span>
      </footer>
    </div>
  );
};

export default LandingPage;
